import { PermissionGate } from '../core/authorization/PermissionGate';
import { PlatformCartPage } from '../features/public/PlatformCart';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ROUTES } from './routes';
import { RequireAuth } from './RequireAuth';
import { LegacyRuntime } from './LegacyRuntime';
import { LegacyShell } from './LegacyShell';
import { RouteBoundary } from './ErrorBoundary';
import { LoginPage } from '../features/auth/LoginPage';
import { LogoutPage } from '../features/auth/LogoutPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { OrgSelectPage } from '../features/auth/OrgSelectPage';
import { ActivityPage } from '../features/activity/ActivityPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { SessionsPage } from '../features/profile/SessionsPage';
import { OrganizationPage } from '../features/organization/OrganizationPage';
import { LaunchProductPage } from '../features/home/LaunchProductPage';
import { SwitchAppPage } from '../features/home/SwitchAppPage';

/* HUB-UI: ported screens keep their legacy grid/edit-modal data bindings.
 * BasicCli supplies customer-facing Hub chrome for them. Native workspace pages
 * use LegacyShell; personal pages use HubLayout without the legacy runtime.
 * LegacyRuntime still supplies the legacy Redux/i18n/notification providers.
 */
import IntegrationPage from '../pages-old/integration/IntegrationPage';
import Currencies from '../pages-old/currencies/Currencies';
import UsersDetailsGrid from '../pages-old/users/UsersDetailsGrid';
import RolesDetailsGrid from '../pages-old/roles/RolesDetailsGrid';
import RoleActionsPage from '../pages-old/roles/RoleActionsPage';
import { RolesPermissionsPage } from '../features/roles/RolesPermissionsPage';
import { PermissionProvider } from '../core/authorization/PermissionProvider';
import NotificationsGrid from '../pages-old/notifications/NotificationsGrid';
import NotificationCategoriesGrid from '../pages-old/notifications/NotificationCategoriesGrid';
import NotificationUserCategoriesGrid from '../pages-old/notifications/NotificationUserCategoriesGrid';
import { LandingPage } from '../features/public/LandingPage';
import { SocialPage } from '../features/public/SocialPage';
import { FeedPage } from '../features/feed/FeedPage';
import { SearchPage } from '../features/public/SearchPage';
import { CompanyPage } from '../features/public/CompanyPage';
import { ForBusinessPage, JobPage, JobsPage } from '../features/public/JobsPages';
import { ProductPage, ProductsPage } from '../features/public/ProductPages';
import { NotFoundPage } from '../features/public/NotFoundPage';
// HUB-UI: personal pages use customer chrome; business pages retain permission-driven menus.
import { HubLayout } from '../features/hub/HubLayout';
import { CustomerAccountPage, CustomerActivityPage, CustomerAddressesPage, CustomerApplicationsPage, CustomerFavouritesPage, CustomerNotificationsPage, CustomerOverviewPage, CustomerPrivacyPage, CustomerTimelinePage } from '../features/hub/CustomerPages';
import {
  OrganizationProfileListingsPage,
  OrganizationProfilePage,
  OrganizationProfilesPage,
} from '../features/hub/OrganizationProfilePages';
import { ApplicantsPage, DirectoryAdminPage, WorkspaceDashboardPage, WorkspaceJobsPage } from '../features/hub/WorkspacePages';
import { BillingPage, MyBusinessesPage, PlanRequestsPage, OrganizationRequestsPage } from '../features/billing/BillingPages';
import { PromotionsPage } from '../features/billing/PromotionsPage';

export default function App() {
  return (
    <PermissionProvider><Routes>
      {/*
        Hub public area (Hub design 06, FE-04) — reachable without signing in. These pages use
        their own light layout and read only the anonymous `/public/*` API; they do not need the
        ported legacy runtime. The former organization landing moved to `/app/feed` below.
      */}
      <Route element={<RouteBoundary title="This page failed to render"><Outlet /></RouteBoundary>}>
        <Route path={ROUTES.landing} element={<LandingPage />} />
        <Route path="/me/cart" element={<PlatformCartPage />} />
        <Route path={ROUTES.search} element={<SearchPage />} />
        <Route path={ROUTES.businesses} element={<SearchPage />} />
        <Route path={ROUTES.products} element={<ProductsPage />} />
        <Route path={ROUTES.product} element={<ProductPage />} />
        <Route path={ROUTES.category} element={<SearchPage />} />
        <Route path={ROUTES.city} element={<SearchPage />} />
        <Route path={ROUTES.company} element={<CompanyPage />} />
        <Route path={ROUTES.jobs} element={<JobsPage />} />
        <Route path={ROUTES.job} element={<JobPage />} />
        <Route path={ROUTES.social} element={<SocialPage />} />
        <Route path={ROUTES.forBusiness} element={<ForBusinessPage />} />
      </Route>
      <Route path={ROUTES.login} element={<LoginPage />} />
      <Route path={ROUTES.logout} element={<LogoutPage />} />
      <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.register} element={<RegisterPage />} />
      <Route path={ROUTES.orgSelect} element={<OrgSelectPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<HubLayout />}>
          <Route path={ROUTES.personalAccount} element={<CustomerOverviewPage />} />
          <Route path={ROUTES.personalActivity} element={<CustomerActivityPage />} />
          <Route path={ROUTES.personalTimeline} element={<CustomerTimelinePage />} />
          <Route path={ROUTES.personalApplications} element={<CustomerApplicationsPage />} />
          <Route path={ROUTES.personalFavourites} element={<CustomerFavouritesPage />} />
          <Route path={ROUTES.personalAddresses} element={<CustomerAddressesPage />} />
          <Route path={ROUTES.personalNotifications} element={<CustomerNotificationsPage />} />
          <Route path={ROUTES.personalSecurity} element={<CustomerAccountPage />} />
          <Route path={ROUTES.personalPrivacy} element={<CustomerPrivacyPage />} />
          <Route path={ROUTES.personalSessions} element={<SessionsPage />} />
          <Route path={ROUTES.switchApp} element={<SwitchAppPage />} />
        </Route>
        {/* Ported legacy screens — each brings its own shell, or is a whole page. */}
        <Route
          element={
            <LegacyRuntime>
              {/*
                A ported screen that throws must not take the app down with it. The boundary
                resets on navigation, so any other link still works and the failure stays
                readable instead of blanking the page.
              */}
              <RouteBoundary title="This ported screen failed to render">
                <Outlet />
              </RouteBoundary>
            </LegacyRuntime>
          }
        >
          {/* The former public `/` landing (organization feed) — now a workspace screen. */}
          <Route path="/integration" element={<PermissionGate scopeBase="platform.integration"><IntegrationPage /></PermissionGate>} />
          <Route path="/currencies" element={<PermissionGate scopeBase="platform.currencies"><Currencies /></PermissionGate>} />
          <Route path={ROUTES.users} element={<PermissionGate scopeBase="platform.memberships"><UsersDetailsGrid /></PermissionGate>} />
          <Route path="/roles/legacy" element={<PermissionGate scopeBase="platform.roles"><RolesDetailsGrid /></PermissionGate>} />
          <Route path="/role-actions/legacy" element={<PermissionGate scopeBase="platform.roles"><RoleActionsPage /></PermissionGate>} />
          <Route path={ROUTES.notifications} element={<NotificationsGrid />} />
          <Route path={ROUTES.notificationCategories} element={<PermissionGate scopeBase="platform.notification-configuration"><NotificationCategoriesGrid /></PermissionGate>} />
          <Route path={ROUTES.notificationUserCategories} element={<PermissionGate scopeBase="platform.notification-rules"><NotificationUserCategoriesGrid /></PermissionGate>} />

          {/*
            Platform-native screens, under the SAME shell as the ported ones. They are plain MUI
            content with no shell of their own, so `LegacyShell` supplies `BasicCli` for them; the
            ported screens above render it themselves and must not be wrapped again.
          */}
          <Route element={<LegacyShell />}>
            <Route path={ROUTES.roles} element={<RolesPermissionsPage />} />
            <Route path="/roles/:roleId" element={<RolesPermissionsPage />} />
            <Route path={ROUTES.roleActions} element={<RolesPermissionsPage />} />
            <Route path={ROUTES.home} element={<WorkspaceDashboardPage />} />
            <Route path={ROUTES.organizationProfiles} element={<PermissionGate scopeBase="platform.company"><OrganizationProfilesPage /></PermissionGate>} />
            <Route path={ROUTES.organizationProfileListings} element={<PermissionGate scopeBase="platform.company"><OrganizationProfileListingsPage /></PermissionGate>} />
            <Route path={ROUTES.organizationProfile} element={<PermissionGate scopeBase="platform.company"><OrganizationProfilePage /></PermissionGate>} />
            <Route path={ROUTES.companyProfile} element={<Navigate to={ROUTES.organizationProfiles} replace />} />
            <Route path={ROUTES.companyListings} element={<Navigate to={ROUTES.organizationProfileListings} replace />} />
            <Route path={ROUTES.workspacePosts} element={<PermissionGate scopeBase="platform.feed"><FeedPage /></PermissionGate>} />
            <Route path={ROUTES.workspaceJobs} element={<PermissionGate scopeBase="platform.jobs"><WorkspaceJobsPage /></PermissionGate>} />
            <Route path={ROUTES.applicants} element={<PermissionGate scopeBase="platform.jobs"><ApplicantsPage /></PermissionGate>} />
            <Route path={ROUTES.directoryAdmin} element={<PermissionGate scopeBase="platform.directory"><DirectoryAdminPage /></PermissionGate>} />
            <Route path={ROUTES.myBusinesses} element={<MyBusinessesPage />} />
            <Route path={ROUTES.billing} element={<BillingPage />} />
            <Route path={ROUTES.billingRequests} element={<PlanRequestsPage />} />
            <Route path={ROUTES.promotions} element={<PromotionsPage />} />
            <Route path={ROUTES.organizationRequests} element={<OrganizationRequestsPage />} />
            <Route path={ROUTES.launchProduct} element={<LaunchProductPage />} />
            <Route path={ROUTES.activity} element={<ActivityPage />} />
            <Route path={ROUTES.profile} element={<ProfilePage />} />
            <Route path={ROUTES.sessions} element={<SessionsPage />} />
            <Route path={ROUTES.organization} element={<OrganizationPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<RouteBoundary title="This page failed to render"><NotFoundPage /></RouteBoundary>} />
    </Routes></PermissionProvider>
  );
}
